import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, ArrowLeft, CheckCircle2, Link2, Facebook, Instagram, Ticket, Globe } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SubmitEvent() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [fbUrl, setFbUrl] = useState("");
  const [igUrl, setIgUrl] = useState("");
  const [ebUrl, setEbUrl] = useState("");
  const [webUrl, setWebUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const anyUrl = [fbUrl, igUrl, ebUrl, webUrl].some(u => u.trim().length > 0);
    // Either enough free-form text OR at least one link is required.
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    if (!anyUrl && text.trim().length < 15) {
      toast.error("Add a link (Facebook / Eventbrite / Instagram / website) OR paste at least 15 characters of event details.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/submit`, {
        email: email.trim(),
        submitter_name: name.trim() || null,
        text: text.trim() || null,
        image_url: imageUrl.trim() || null,
        fb_url: fbUrl.trim() || null,
        ig_url: igUrl.trim() || null,
        eventbrite_url: ebUrl.trim() || null,
        website_url: webUrl.trim() || null,
      });
      setResult(r.data);
      toast.success(r.data.message);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 py-16 px-4">
        <div className="mx-auto max-w-xl bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <CheckCircle2 size={48} className="mx-auto text-emerald-600 mb-4" />
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Thanks — submission received!</h1>
          <p className="text-slate-600 mb-6">
            {result.events_created} event{result.events_created === 1 ? "" : "s"} pulled from your submission
            and sent to our review queue. It usually goes live within 24 hours.
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/">
              <Button variant="outline" data-testid="back-to-calendar-btn">
                <ArrowLeft size={14} className="mr-1" /> Back to calendar
              </Button>
            </Link>
            <Button
              onClick={() => { setResult(null); setText(""); setImageUrl(""); setFbUrl(""); setIgUrl(""); setEbUrl(""); setWebUrl(""); }}
              className="bg-[#1A3C5A] text-white hover:bg-[#12293e]"
              data-testid="submit-another-btn"
            >
              Submit another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="mx-auto max-w-xl">
        <Link to="/" className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 text-sm mb-6" data-testid="back-link">
          <ArrowLeft size={14} /> Back to calendar
        </Link>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <h1 className="text-3xl font-semibold text-slate-900 mb-2">Submit an event</h1>
          <p className="text-slate-600 mb-8">
            Paste a Facebook, Eventbrite, Instagram, or website link and we'll auto-fill everything —
            or type the event details yourself. Approved events appear on the calendar within 24 hours.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email">Your email <span className="text-red-500">*</span></Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="submit-email-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                We only use this if we need to clarify something about your event.
              </p>
            </div>

            <div>
              <Label htmlFor="name">Your name (optional)</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                data-testid="submit-name-input"
              />
            </div>

            {/* Smart link inputs — paste ONE link and we'll auto-extract the event.
                Text description below becomes optional if any link is filled. */}
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
              <div className="flex items-center gap-2 text-emerald-800">
                <Link2 size={15} strokeWidth={2.25} />
                <span className="text-sm font-semibold">Have a link? We'll do the rest.</span>
              </div>
              <p className="text-xs text-slate-600 -mt-1">
                Paste any one of the links below and our scraper will pull the event details for you.
                No need to type anything else (except your email).
              </p>

              <div>
                <Label htmlFor="fb-url" className="flex items-center gap-1.5 text-xs">
                  <Facebook size={12} strokeWidth={2.25} className="text-[#1877F2]" /> Facebook event link
                </Label>
                <Input
                  id="fb-url"
                  type="url"
                  value={fbUrl}
                  onChange={(e) => setFbUrl(e.target.value)}
                  placeholder="https://www.facebook.com/events/..."
                  data-testid="submit-fb-url-input"
                />
              </div>

              <div>
                <Label htmlFor="eb-url" className="flex items-center gap-1.5 text-xs">
                  <Ticket size={12} strokeWidth={2.25} className="text-orange-600" /> Eventbrite link
                </Label>
                <Input
                  id="eb-url"
                  type="url"
                  value={ebUrl}
                  onChange={(e) => setEbUrl(e.target.value)}
                  placeholder="https://www.eventbrite.com/e/..."
                  data-testid="submit-eb-url-input"
                />
              </div>

              <div>
                <Label htmlFor="ig-url" className="flex items-center gap-1.5 text-xs">
                  <Instagram size={12} strokeWidth={2.25} className="text-pink-600" /> Instagram post link
                </Label>
                <Input
                  id="ig-url"
                  type="url"
                  value={igUrl}
                  onChange={(e) => setIgUrl(e.target.value)}
                  placeholder="https://www.instagram.com/p/..."
                  data-testid="submit-ig-url-input"
                />
              </div>

              <div>
                <Label htmlFor="web-url" className="flex items-center gap-1.5 text-xs">
                  <Globe size={12} strokeWidth={2.25} className="text-slate-600" /> Any other website
                </Label>
                <Input
                  id="web-url"
                  type="url"
                  value={webUrl}
                  onChange={(e) => setWebUrl(e.target.value)}
                  placeholder="https://your-venue.com/events/..."
                  data-testid="submit-web-url-input"
                />
              </div>
            </div>

            <div className="relative flex items-center py-1">
              <div className="flex-grow border-t border-slate-200" />
              <span className="mx-3 text-xs uppercase tracking-widest text-slate-400 font-semibold">or type it out</span>
              <div className="flex-grow border-t border-slate-200" />
            </div>

            <div>
              <Label htmlFor="text">Event details <span className="text-slate-400 text-xs">(optional if you added a link above)</span></Label>
              <Textarea
                id="text"
                rows={8}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={`Example:\n\nSummer Concert at Bug Light Park\nSaturday August 10, 2026, 6-9pm\n\nFree outdoor concert featuring the Casco Bay Big Band. Bring a picnic and a chair!\n\n1 Breakwater Dr, South Portland, ME`}
                data-testid="submit-text-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Include date, time, and venue — that's all our AI needs.
              </p>
            </div>

            <div>
              <Label htmlFor="image">Image URL (optional)</Label>
              <Input
                id="image"
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://..."
                data-testid="submit-image-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1A3C5A] text-white hover:bg-[#12293e]"
              data-testid="submit-event-btn"
            >
              <Send size={14} className="mr-1" />
              {submitting ? "Processing…" : "Submit event"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
